import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { PreautorisationSelectors } from '../../state/preautorisation.selectors';
import { TypeProjet } from '../../../../shared/constants/type-projet.enum';
import { EMPRUNTEUR_PRINCIPAL, EMPRUNTEUR_SECONDAIRE, STEP_VALUES } from './preautorisation-cybermetrie.data';
import { get, omit } from 'lodash';
import { StepCybermetrieEnum } from './step-cybermetrie.enum';
import { Evenement, EvenementDataLayer } from '@oel/js-cybermetrie';
import { EventCategoryEnum } from './event-category.enum';
import { EventFormulaireModel } from './event-formulaire.model';
import { EventActionEnum } from './event-action.enum';
import { GtmService } from '../../../../shared/services/gtm.service';
import { FrequenceTermeEnum } from '../../models/taux-garanti.model';
import { SousTypeTauxEnum } from '../../../shared/constants/sous-type-taux.enum';
import { StatusPreautorisation } from '../../../../shared/modeles/variables';

@Injectable({
  providedIn: 'any'
})
export class PreautorisationCybermetrieService {
  constructor(private readonly gtmService: GtmService, private readonly store: Store) {}

  private get extraAttributEmprunteur() {
    return this.store.selectSnapshot(PreautorisationSelectors.isCoemprunteur) ? 'coemprunteur' : 'emprunteurUnique';
  }

  private get transactionId() {
    return this.store.selectSnapshot(PreautorisationSelectors.idCybermetrie);
  }

  private get labelPrefix() {
    switch (this.store.selectSnapshot(PreautorisationSelectors.typePreautorisation)) {
      case TypeProjet.PremierAcheteur:
        return 'preautorisation - premiere maison';
      case TypeProjet.ExperimenteRemplacer:
        return 'preautorisation - remplacer residence';
      case TypeProjet.ExperimenteSecondaire:
        return 'preautorisation - residence secondaire';
      default:
        return '';
    }
  }

  private pushEvent(data: EvenementDataLayer, dimensionsPersonnalisees: Record<string, string> = {}) {
    this.gtmService.pushEvent(
      data,
      new Map(
        Object.entries({
          transactionId: this.transactionId,
          extraAttribut: this.extraAttributEmprunteur,
          ...dimensionsPersonnalisees
        })
      )
    );
  }

  pushFormInterractionEvent(action: string, label: string, data: EventFormulaireModel = {}) {
    this.pushEvent(
      {
        event: Evenement.InterractionsPages,
        category: EventCategoryEnum.InteractionForm,
        action: `${action} | saisis`,
        label: `${this.labelPrefix} - ${label}`,
        value: 0,
        nonInteraction: false
      },
      omit(data, 'value')
    );
  }

  private getStepActionValue(step: StepCybermetrieEnum) {
    const typeProjet = this.store.selectSnapshot(PreautorisationSelectors.typePreautorisation);
    const typeEmprunteur = this.store.selectSnapshot(PreautorisationSelectors.isEmprunteurSecondaire)
      ? EMPRUNTEUR_SECONDAIRE
      : EMPRUNTEUR_PRINCIPAL;

    return get(STEP_VALUES, `${typeProjet}.${typeEmprunteur}.${step}`, null);
  }

  pushClickEvent(label: string, data: EventFormulaireModel = {}) {
    this.pushEvent(
      {
        event: Evenement.InterractionsPages,
        category: EventCategoryEnum.ParcoursHabitation,
        action: EventActionEnum.Clic,
        label: `${this.labelPrefix} - ${label}`,
        value: Math.ceil(data.value || 0),
        nonInteraction: false
      },
      omit(data, 'value')
    );
  }

  pushTelechargementEvent(label: string) {
    this.pushEvent({
      event: Evenement.InterractionsPages,
      category: EventCategoryEnum.Telechargement,
      action: EventActionEnum.Clic,
      label: `${this.labelPrefix} - ${label}`,
      value: 0,
      nonInteraction: false
    });
  }

  pushFormulaireEvent(action: string, data: EventFormulaireModel = {}) {
    this.pushEvent(
      {
        event: Evenement.InterractionsPages,
        category: EventCategoryEnum.Formulaire,
        action: action,
        label: `frm - ${this.labelPrefix}`,
        value: Math.ceil(data.value || 0),
        nonInteraction: false
      },
      omit(data, 'value')
    );
  }

  pushStepFormulaireEvent(step: StepCybermetrieEnum, data: EventFormulaireModel = {}) {
    const stepAction = this.getStepActionValue(step);
    if (stepAction) {
      this.pushFormulaireEvent(stepAction, data);
    }
  }

  pushStepFormInteraction(step: StepCybermetrieEnum, action: string, label: string) {
    const stepAction = this.getStepActionValue(step);
    if (stepAction) {
      this.pushFormInterractionEvent(`${stepAction} | ${action}`, label);
    }
  }

  pushTiroirEvent(label: string) {
    this.pushEvent({
      event: Evenement.InterractionsPages,
      category: EventCategoryEnum.Tiroir,
      action: EventActionEnum.Ouverture,
      label: `${this.labelPrefix} - ${label}`,
      value: 0,
      nonInteraction: false
    });
  }

  pushCompletionEvent(statut: StatusPreautorisation) {
    switch (statut) {
      case StatusPreautorisation.RefuseeSimplifie:
        this.pushFormulaireEvent(EventActionEnum.Sortie, { messageErreur: 'Refuser par le systeme' });
        break;
      case StatusPreautorisation.ContreOffreRefusee:
        this.pushFormulaireEvent(EventActionEnum.Sortie, { messageErreur: 'Refuser la contre-offre' });
        break;
      case StatusPreautorisation.Acceptee:
        this.pushFormulaireEvent(EventActionEnum.Completer, {
          value: this.store.selectSnapshot(PreautorisationSelectors.montantPreautorise),
          choixTerme: this.getChoixTerme()
        });
        break;
      case StatusPreautorisation.ContreOffreAcceptee:
        this.pushFormulaireEvent(EventActionEnum.Completer, {
          value: this.store.selectSnapshot(PreautorisationSelectors.montantPreautorise),
          choixTerme: this.getChoixTerme(),
          extraAttribut2: this.calculerPourcentageEcartContreOffre().toString()
        });
        break;
    }
  }

  public calculerPourcentageEcartContreOffre() {
    const montantDemande = this.store.selectSnapshot(PreautorisationSelectors.montantDemande);
    const montantPropose = this.store.selectSnapshot(PreautorisationSelectors.montantPropose);
    return Math.ceil(((montantDemande - montantPropose) / montantDemande) * 100);
  }

  public getChoixTerme() {
    const taux = this.store.selectSnapshot(PreautorisationSelectors.tauxSelectionne);
    const nombreMois = taux.frequenceTerme === FrequenceTermeEnum.Ans ? taux.nbrUniteTerme * 12 : taux.nbrUniteTerme;
    const typeTaux =
      taux.sousTypeTaux === SousTypeTauxEnum.FixeRegulier || SousTypeTauxEnum.FixeRevisable ? 'fixe' : 'variable';

    return `${nombreMois} mois | ${typeTaux}`;
  }
}
